package br.gov.go.pge.permanenciaweb.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.gov.go.pge.permanenciaweb.shared.ErrorDto
import br.gov.go.pge.permanenciaweb.shared.EventDto
import br.gov.go.pge.permanenciaweb.shared.MeDto
import br.gov.go.pge.permanenciaweb.shared.OccupantDto
import br.gov.go.pge.permanenciaweb.shared.RemovalRequest
import br.gov.go.pge.permanenciaweb.shared.SemSaidaRowDto
import br.gov.go.pge.permanenciaweb.shared.SnapshotDto
import br.gov.go.pge.permanenciaweb.shared.WsEnvelope
import br.gov.go.pge.permanenciaweb.site.AppState
import br.gov.go.pge.permanenciaweb.site.LocalAppState
import br.gov.go.pge.permanenciaweb.site.components.AppHeader
import br.gov.go.pge.permanenciaweb.site.components.ColumnFilter
import br.gov.go.pge.permanenciaweb.site.components.Criterion
import br.gov.go.pge.permanenciaweb.site.components.EventSortField
import br.gov.go.pge.permanenciaweb.site.components.EventsPanel
import br.gov.go.pge.permanenciaweb.site.components.FilterKey
import br.gov.go.pge.permanenciaweb.site.components.FilterScope
import br.gov.go.pge.permanenciaweb.site.components.QuickFilterId
import br.gov.go.pge.permanenciaweb.site.components.rosterRows
import br.gov.go.pge.permanenciaweb.site.components.KpiStrip
import br.gov.go.pge.permanenciaweb.site.components.LiveStatus
import br.gov.go.pge.permanenciaweb.site.components.PhotoDialog
import br.gov.go.pge.permanenciaweb.site.components.RemovalDialog
import br.gov.go.pge.permanenciaweb.site.components.RemovedPanel
import br.gov.go.pge.permanenciaweb.site.components.RosterLayoutStyle
import br.gov.go.pge.permanenciaweb.site.components.RosterPanel
import br.gov.go.pge.permanenciaweb.site.components.RosterSortField
import br.gov.go.pge.permanenciaweb.site.components.SemSaidaDialog
import br.gov.go.pge.permanenciaweb.site.components.SemSaidaPanel
import br.gov.go.pge.permanenciaweb.site.components.SemSaidaSortField
import br.gov.go.pge.permanenciaweb.site.components.TabNav
import br.gov.go.pge.permanenciaweb.site.components.Toast
import br.gov.go.pge.permanenciaweb.site.components.facetOptions
import br.gov.go.pge.permanenciaweb.site.components.filterKey
import br.gov.go.pge.permanenciaweb.site.components.hasAny
import br.gov.go.pge.permanenciaweb.site.components.toggledSort
import com.varabyte.kobweb.browser.http.bodyAsBytes
import com.varabyte.kobweb.browser.http.bodyOf
import com.varabyte.kobweb.browser.http.http
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.jetbrains.compose.web.dom.Main
import org.w3c.dom.WebSocket
import org.w3c.dom.url.URLSearchParams
import org.w3c.fetch.Response

/*
 * Dashboard page: state, WebSocket/bootstrap/actions and the thin composition of the extracted
 * widgets (each widget owns its `CssStyle`s). Strings and behavior are ported 1:1 from the old
 * static `index.html` + `app.js`.
 */

private val json = Json { ignoreUnknownKeys = true }

/** Batch size for incremental event rendering (step 5). */
internal const val EVENTS_BATCH = 100

/** Roster rows per page. */
internal const val ROSTER_PAGE_SIZE = 10

/** Dashboard view selected by `?aba=...`; [queryValue] null is the default roster view. */
internal enum class Tab(
    val queryValue: String?,
    val navLabel: String,
    val heading: String,
    val subtitle: String?,
    val documentTitle: String,
) {
    ROSTER(
        queryValue = null,
        navLabel = "Painel",
        heading = "Possíveis pessoas no prédio — agora",
        subtitle = "Última marcação de hoje = entrada. Dados de catraca; imprecisões são possíveis.",
        documentTitle = "Possíveis pessoas no prédio — agora",
    ),
    EVENTS(
        queryValue = "eventos",
        navLabel = "Eventos",
        heading = "Eventos de hoje",
        subtitle = "Entradas e saídas registradas hoje. Dados de catraca; imprecisões são possíveis.",
        documentTitle = "Eventos de hoje — Permanência no Prédio",
    ),
    SEM_SAIDA(
        queryValue = "sem-saida",
        navLabel = "Sem saída",
        heading = "Sem saída (30 dias)",
        subtitle = "Pessoas com um ou mais dias sem saída registrada nos últimos 30 dias. " +
            "Dados de catraca; imprecisões são possíveis.",
        documentTitle = "Sem saída (30 dias) — Permanência no Prédio",
    );

    companion object {
        fun fromQuery(value: String?): Tab = when (value?.trim()?.lowercase()) {
            "eventos", "events" -> EVENTS
            "sem-saida", "sem_saida", "sem-saída" -> SEM_SAIDA
            else -> ROSTER
        }
    }

    fun matchesQuery(raw: String?): Boolean = raw?.trim() == queryValue
}

/** Builds `/` or `/?aba=...` while preserving unrelated query parameters. */
internal fun tabHref(tab: Tab, currentSearch: String): String {
    val params = URLSearchParams(currentSearch)
    if (tab.queryValue == null) {
        params.delete("aba")
    } else {
        params.set("aba", tab.queryValue)
    }
    val query = params.toString()
    return if (query.isEmpty()) "/" else "/?$query"
}

/**
 * All dashboard state. Initial values deliberately mirror the old page's initial DOM
 * (KPI placeholders are `–`, badge says "conectando…", empty lists), which also makes the
 * export prerender a deterministic shell.
 */
internal class DashboardState {
    var snapshot by mutableStateOf<SnapshotDto?>(null)
    var liveStatus by mutableStateOf(LiveStatus.CONNECTING)

    /** Signed-in user; `null` until the first bootstrap. Gates the admin menu entry. */
    var me by mutableStateOf<MeDto?>(null)
    var events by mutableStateOf<List<EventDto>>(emptyList())

    /**
     * Per-column filters, shared by both tables: a filter set on `ANDAR` narrows both the roster
     * and the events tab (see [FilterKey]). Inactive columns are never stored.
     */
    var filters by mutableStateOf<Map<FilterKey, ColumnFilter>>(emptyMap())
        private set

    /** True when any filter would reduce the lists (gates the "no results" messages). */
    val filtersActive: Boolean
        get() = filters.isNotEmpty() || quickFilters.isNotEmpty()

    /**
     * KPI cards that act as filters (currently roster-only). Kept apart from [filters] because a
     * card constrains blankness ("sem andar"), which a column filter cannot express.
     */
    var quickFilters by mutableStateOf<Set<QuickFilterId>>(emptySet())
        private set

    /** Toggles a roster card filter; the cards only render on the roster tab. */
    fun toggleQuickFilter(id: QuickFilterId) {
        quickFilters = if (id !in quickFilters) quickFilters + id else quickFilters - id
        resetEventsPaging()
    }

    /** The removal history starts collapsed; its header shows the count. */
    var historyExpanded by mutableStateOf(false)

    /** How many event rows are rendered; step 5 grows this while scrolling. */
    var eventsVisibleCount by mutableStateOf(EVENTS_BATCH)

    /** Rows of the 30-day missed-exit report; loaded when that tab is opened. */
    var semSaidaRows by mutableStateOf<List<SemSaidaRowDto>>(emptyList())
    var semSaidaPage by mutableStateOf(1)
    var semSaidaLoaded by mutableStateOf(false)
    var semSaidaLoading by mutableStateOf(false)
    var semSaidaLoadError by mutableStateOf<String?>(null)
    var semSaidaSort by mutableStateOf<List<Criterion<SemSaidaSortField>>>(emptyList())
    var semSaidaDetailId by mutableStateOf<String?>(null)

    /** Server-side events revision; the events tab refetches whenever it changes. */
    var eventsVersion by mutableStateOf<Long?>(null)

    /** Current roster page; filters and sorts rewind it to the first page. */
    var rosterPage by mutableStateOf(1)

    /** Multi-column sort, ordered by priority; an empty list keeps the server order. */
    var rosterSort by mutableStateOf<List<Criterion<RosterSortField>>>(emptyList())
    var eventsSort by mutableStateOf<List<Criterion<EventSortField>>>(emptyList())

    fun toggleRosterSort(field: RosterSortField, shift: Boolean) {
        rosterSort = toggledSort(rosterSort, field, shift)
        rosterPage = 1
    }

    fun toggleEventsSort(field: EventSortField, shift: Boolean) {
        eventsSort = toggledSort(eventsSort, field, shift)
        resetEventsPaging()
    }

    fun toggleSemSaidaSort(field: SemSaidaSortField, shift: Boolean) {
        semSaidaSort = toggledSort(semSaidaSort, field, shift)
        semSaidaPage = 1
    }

    fun openSemSaidaDetail(idpessoa: String) {
        semSaidaDetailId = idpessoa
    }

    fun closeSemSaidaDetail() {
        semSaidaDetailId = null
    }

    /** Filter of one roster column (shared keys resolve to the same entry as the events tab). */
    fun filterOf(field: RosterSortField): ColumnFilter = filters[field.filterKey] ?: ColumnFilter.Empty

    /** Filter of one events column. */
    fun filterOf(field: EventSortField): ColumnFilter = filters[field.filterKey] ?: ColumnFilter.Empty

    /** Sets (or clears, when the filter is inactive) the filter of one column. */
    fun updateFilter(key: FilterKey, filter: ColumnFilter) = putFilter(key, filter)

    /** True when [scope] actually narrows something ("Limpar tudo" enabled state). */
    fun hasFilters(scope: FilterScope): Boolean =
        scope.keys.any { filters[it]?.isActive == true } ||
            (scope.includesQuickFilters && quickFilters.isNotEmpty())

    /** Clears the shared filters plus the given table's own columns (and its KPI cards). */
    fun clearFilters(scope: FilterScope) {
        filters = filters - scope.keys
        if (scope.includesQuickFilters) quickFilters = emptySet()
        resetEventsPaging()
    }

    private fun putFilter(key: FilterKey, filter: ColumnFilter) {
        filters = if (filter.isStored) filters + (key to filter) else filters - key
        resetEventsPaging()
    }

    /**
     * Every filter change rewinds incremental event rendering and the roster to the first page.
     * Tab switches no longer touch it: filters are shared, so the position is still valid.
     */
    fun resetEventsPaging() {
        eventsVisibleCount = EVENTS_BATCH
        rosterPage = 1
        semSaidaPage = 1
    }

    /** Appends one batch of events, never past the current filtered total. */
    fun loadMoreEvents(total: Int) {
        eventsVisibleCount = (eventsVisibleCount + EVENTS_BATCH).coerceAtMost(total)
    }

    var pendingRemoval by mutableStateOf<OccupantDto?>(null)
    var removalNote by mutableStateOf("")

    /** Occupant whose photo dialog is open; null means no dialog and no image fetch. */
    var pendingPhotos by mutableStateOf<OccupantDto?>(null)

    fun openPhotos(person: OccupantDto) {
        pendingPhotos = person
    }

    fun closePhotos() {
        pendingPhotos = null
    }

    var toastMessage by mutableStateOf("")
    var toastVisible by mutableStateOf(false)
    var toastSeq by mutableStateOf(0)

    /** `null` until the first snapshot arrives; the first snapshot must never toast. */
    var knownResolvedIds by mutableStateOf<Set<Long>?>(null)

    fun toast(message: String) {
        toastMessage = message
        toastSeq++
    }

    /** WS frame: update the roster snapshot and remember the newest events revision. */
    fun applyEnvelope(envelope: WsEnvelope) {
        applySnapshot(envelope.snapshot)
        eventsVersion = envelope.eventsVersion
    }

    fun applySnapshot(snapshot: SnapshotDto) {
        this.snapshot = snapshot

        val resolved = snapshot.resolvedRemovals
        val ids = resolved.map { it.removalId }.toSet()
        val known = knownResolvedIds
        if (known == null) {
            // First snapshot: remember what is already known, no toast storm.
            knownResolvedIds = ids
        } else {
            for (r in resolved) {
                if (r.resolution == "REENTERED" && r.removalId !in known) {
                    toast("⚠ ${r.nome} voltou a entrar após remoção manual.")
                }
            }
            knownResolvedIds = ids
        }
    }

    fun openRemoval(person: OccupantDto) {
        pendingRemoval = person
        removalNote = ""
    }

    /**
     * Clears modal/transient UI when the page leaves composition.
     *
     * With SPA navigation the state object survives the page. A dialog left open (or a toast
     * mid-timer) would otherwise reappear the next time the dashboard is mounted.
     */
    fun resetTransientUi() {
        pendingRemoval = null
        removalNote = ""
        pendingPhotos = null
        semSaidaDetailId = null
        toastVisible = false
    }
}

@Page
@Composable
fun Index() {
    val appState = LocalAppState.current
    val state = appState.dashboard
    val scope = rememberCoroutineScope()
    val pageContext = rememberPageContext()
    val rawTab = pageContext.route.queryParams["aba"]
    val tab = Tab.fromQuery(rawTab)

    // Query-only navigation does not remount the page, but PageContext.route is observable, so
    // changes from the tab buttons, reloads and Back/Forward all re-enter this composition.
    LaunchedEffect(rawTab, tab) {
        if (!tab.matchesQuery(rawTab)) {
            pageContext.router.navigateTo(
                tabHref(tab, window.location.search),
                UpdateHistoryMode.REPLACE,
            )
        }
    }

    SideEffect {
        if (document.title != tab.documentTitle) document.title = tab.documentTitle
    }

    LaunchedEffect(Unit) {
        if (AppGlobals.isExporting) return@LaunchedEffect
        // First mount loads the snapshot; later SPA visits only revalidate the session. The
        // WebSocket loop is owned by AppState, so it survives a trip to another page.
        val ready = if (state.snapshot == null) bootstrap(state) else refreshMe(state)
        if (ready) appState.startLiveUpdates()
    }

    DisposableEffect(Unit) {
        onDispose { state.resetTransientUi() }
    }

    val toastSeq = state.toastSeq
    LaunchedEffect(toastSeq) {
        if (toastSeq == 0) return@LaunchedEffect
        state.toastVisible = true
        delay(4000)
        state.toastVisible = false
    }

    Dashboard(
        state = state,
        tab = tab,
        onSelectTab = { target ->
            if (target != tab) {
                state.resetEventsPaging()
                pageContext.router.navigateTo(
                    tabHref(target, window.location.search),
                    UpdateHistoryMode.REPLACE,
                )
            }
        },
    ) { action -> scope.launch { action() } }
}

@Composable
private fun Dashboard(
    state: DashboardState,
    tab: Tab,
    onSelectTab: (Tab) -> Unit,
    launchAction: ((suspend () -> Unit) -> Unit),
) {
    val snapshot = state.snapshot
    val occupants = snapshot?.occupants ?: emptyList()

    // Union of occupant + (lazily loaded) event values; recomputed only when either list changes.
    val facets = remember(occupants, state.events) { facetOptions(occupants, state.events) }

    AppHeader(snapshot, state.liveStatus, state.me, tab.heading, tab.subtitle)

    TabNav(selected = tab, onSelect = onSelectTab)

    when (tab) {
        Tab.ROSTER -> {
            KpiStrip(snapshot, state, state.rosterRows(occupants))
            RosterView(state, occupants, facets, launchAction)
        }
        Tab.EVENTS -> EventsPanel(state, facets)
        Tab.SEM_SAIDA -> SemSaidaPanel(state)
    }

    RemovalDialog(state, launchAction)
    SemSaidaDialog(state)
    PhotoDialog(state)

    Toast(state.toastMessage, state.toastVisible)
}

@Composable
private fun RosterView(
    state: DashboardState,
    occupants: List<OccupantDto>,
    facets: Map<FilterKey, List<String>>,
    launchAction: ((suspend () -> Unit) -> Unit),
) {
    Main(attrs = RosterLayoutStyle.toAttrs()) {
        RosterPanel(
            state = state,
            occupants = occupants,
            facets = facets,
            onSemSaida = { state.openSemSaidaDetail(it.idpessoa) },
            onPhotos = { state.openPhotos(it) },
        )
        RemovedPanel(state, launchAction)
    }
}

// ------------------------------------------------------------------ live in

private suspend fun bootstrap(state: DashboardState): Boolean {
    // The session comes first: capabilities decide whether this page is the right landing page
    // for the user (leadership without report access belongs to the form, not the roster).
    val meResponse = try {
        window.http.get("/api/me")
    } catch (t: Throwable) {
        state.toast("Não foi possível carregar o painel.")
        return false
    }
    if (meResponse.status.toInt() == 401 || meResponse.redirected) {
        redirectToLogin()
        return false
    }
    state.me = if (meResponse.ok) {
        runCatching {
            json.decodeFromString<MeDto>(meResponse.bodyAsBytes().decodeToString())
        }.getOrNull()
    } else {
        null
    }

    val response = try {
        window.http.get("/api/roster")
    } catch (t: Throwable) {
        state.toast("Não foi possível carregar o painel.")
        return false
    }
    if (response.status.toInt() == 401 || response.redirected) {
        redirectToLogin()
        return false
    }
    if (response.status.toInt() == 403) {
        // Pre-created leadership: the form is their page until an admin releases the report.
        val permits = state.me?.let { it.canCreateStayPermits || it.canViewStayPermits } == true
        window.location.href = if (permits) "/liberacoes" else "/sem-acesso"
        return false
    }
    if (!response.ok) {
        state.toast("Falha ao carregar o painel.")
        return false
    }

    val snapshot = runCatching {
        json.decodeFromString<SnapshotDto>(response.bodyAsBytes().decodeToString())
    }.getOrNull()
    if (snapshot == null) {
        state.toast("Falha ao carregar o painel.")
        return false
    }
    state.applySnapshot(snapshot)
    return true
}

/**
 * SPA re-entry: the snapshot is still in [AppState], so only the session/permission state needs
 * a round trip. This catches expiry or a revoked `VIEW_REPORT` without refetching the roster.
 */
private suspend fun refreshMe(state: DashboardState): Boolean {
    val response = try {
        window.http.get("/api/me")
    } catch (t: Throwable) {
        state.toast("Não foi possível carregar o painel.")
        return false
    }
    if (response.status.toInt() == 401 || response.redirected) {
        redirectToLogin()
        return false
    }
    state.me = if (response.ok) {
        runCatching {
            json.decodeFromString<MeDto>(response.bodyAsBytes().decodeToString())
        }.getOrNull()
    } else {
        null
    }
    if (state.me?.canViewReport != true) {
        val permits = state.me?.let { it.canCreateStayPermits || it.canViewStayPermits } == true
        window.location.href = if (permits) "/liberacoes" else "/sem-acesso"
        return false
    }
    return true
}

/**
 * Starts the WebSocket loop once, in [AppState]'s scope. A page-scoped `LaunchedEffect` would
 * cancel the socket every time the user opens another page.
 */
internal fun AppState.startLiveUpdates() {
    if (liveStarted) return
    liveStarted = true
    scope.launch { connect(dashboard) }
}

private suspend fun connect(state: DashboardState) {
    while (true) {
        val closed = CompletableDeferred<Unit>()
        val scheme = if (window.location.protocol == "https:") "wss" else "ws"
        val socket = WebSocket("$scheme://${window.location.host}/ws")

        socket.onmessage = { event ->
            val envelope = runCatching {
                json.decodeFromString<WsEnvelope>(event.data.toString())
            }.getOrNull()
            if (envelope != null && envelope.type == "snapshot") {
                state.applyEnvelope(envelope)
            }
            state.liveStatus = LiveStatus.ONLINE
        }
        socket.onclose = {
            state.liveStatus = LiveStatus.OFFLINE
            closed.complete(Unit)
        }
        socket.onerror = { socket.close() }

        try {
            closed.await()
        } finally {
            if (socket.readyState != WebSocket.CLOSED && socket.readyState != WebSocket.CLOSING) {
                socket.close()
            }
        }
        delay(2000)
    }
}

private fun redirectToLogin() {
    window.location.href = "/login"
}

// ------------------------------------------------------------------ actions

internal suspend fun DashboardState.remove(person: OccupantDto) {
    val response = runCatching {
        window.http.post(
            "/api/removals",
            body = bodyOf(
                json.encodeToString(RemovalRequest(person.idpessoa, removalNote.trim().ifEmpty { null })),
                "application/json",
            ),
        )
    }.getOrNull() ?: return

    if (response.status.toInt() == 401) {
        redirectToLogin()
        return
    }
    if (!response.ok) {
        toast(errorMessage(response))
        return
    }
    toast("${person.nome} removido do painel.")
}

internal suspend fun DashboardState.undoRemoval(id: Long) {
    val response = runCatching {
        window.http.post("/api/removals/$id/undo")
    }.getOrNull() ?: return

    if (response.status.toInt() == 401) {
        redirectToLogin()
        return
    }
    if (!response.ok) {
        toast(errorMessage(response))
        return
    }
    toast("Remoção desfeita.")
}

internal suspend fun DashboardState.loadSemSaida() {
    if (semSaidaLoading) return

    semSaidaLoading = true
    semSaidaLoadError = null
    try {
        val response = runCatching {
            window.http.get("/api/presence/sem-saida")
        }.getOrNull()

        if (response == null) {
            semSaidaLoadError = "Não foi possível carregar os fechamentos."
            return
        }

        if (response.status.toInt() == 401) {
            redirectToLogin()
            return
        }
        if (!response.ok) {
            val message = errorMessage(response)
            semSaidaLoadError = message
            toast(message)
            return
        }

        val rows = runCatching {
            json.decodeFromString<List<SemSaidaRowDto>>(response.bodyAsBytes().decodeToString())
        }.getOrNull()

        if (rows == null) {
            semSaidaLoadError = "Resposta inválida ao carregar os fechamentos."
            return
        }

        this.semSaidaRows = rows
        this.semSaidaPage = 1
        this.semSaidaLoaded = true
    } finally {
        semSaidaLoading = false
    }
}

internal suspend fun DashboardState.loadEvents() {
    val response = runCatching {
        window.http.get("/api/events")
    }.getOrNull() ?: return

    if (response.status.toInt() == 401) {
        redirectToLogin()
        return
    }
    if (!response.ok) {
        toast(errorMessage(response))
        return
    }

    val events = runCatching {
        json.decodeFromString<List<EventDto>>(response.bodyAsBytes().decodeToString())
    }.getOrNull() ?: return

    this.events = events
}

private suspend fun errorMessage(response: Response): String {
    return try {
        json.decodeFromString<ErrorDto>(response.bodyAsBytes(requireOk = false).decodeToString()).error
    } catch (t: Throwable) {
        "erro ${response.status}"
    }
}
